import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ShieldCheck,
  Activity,
  ServerCog,
  Network,
  Tag,
  Cpu,
  Users,
  Cloud,
  Sparkles,
  AlertTriangle,
  Eye,
  CheckCircle2,
} from "lucide-react";
import { LogoLockup, LogoMark } from "../components/site/Logo";

// Scene timeline (seconds)
const SCENES = [
  { id: 1, duration: 8,  build: SceneIntro },
  { id: 2, duration: 14, build: SceneVAPT },
  { id: 3, duration: 12, build: SceneThreat },
  { id: 4, duration: 14, build: SceneInfra },
  { id: 5, duration: 12, build: SceneFutureOps },
  { id: 6, duration: 14, build: SceneStaffing },
  { id: 7, duration: 16, build: SceneWhiteLabel },
  { id: 8, duration: 10, build: SceneFinal },
];

const TOTAL = SCENES.reduce((a, s) => a + s.duration, 0); // 100s
const STARTS = SCENES.reduce((acc, s) => {
  const last = acc.length ? acc[acc.length - 1] : 0;
  acc.push(last + s.duration);
  return acc;
}, []);

export default function Reel() {
  const [t, setT] = useState(0); // elapsed seconds
  const [running, setRunning] = useState(false);

  // Read ?auto=1 to start immediately (used during recording)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("auto") === "1") {
      setRunning(true);
    }
  }, []);

  useEffect(() => {
    if (!running) return;
    const start = performance.now();
    let raf;
    const tick = () => {
      const elapsed = (performance.now() - start) / 1000;
      setT(elapsed);
      if (elapsed < TOTAL + 0.5) {
        raf = requestAnimationFrame(tick);
      } else {
        setRunning(false);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running]);

  const currentIdx = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < SCENES.length; i++) {
      acc += SCENES[i].duration;
      if (t < acc) return i;
    }
    return SCENES.length - 1;
  }, [t]);

  const sceneT = t - (currentIdx > 0 ? STARTS[currentIdx - 1] : 0);
  const sceneDur = SCENES[currentIdx].duration;
  const sceneProgress = Math.min(1, sceneT / sceneDur);

  return (
    <div className="w-screen h-screen bg-[#050507] text-white overflow-hidden relative font-display"
         style={{fontFamily: "'Unbounded', sans-serif"}}
         data-testid="reel-page"
    >
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050507]" />

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-8 py-5">
        <LogoLockup variant="dark" size={28} />
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#00F0FF] flex items-center gap-3">
          <span className="w-1.5 h-1.5 bg-[#39FF14] pulse-dot" />
          QUASECT · CLIENT REEL · {String(Math.floor(t)).padStart(2,"0")}/{TOTAL}s
        </div>
      </div>

      {/* Scene */}
      <div className="absolute inset-0 z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIdx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            {SCENES[currentIdx].build({ t: sceneT, p: sceneProgress })}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom progress + brand */}
      <div className="absolute bottom-0 inset-x-0 z-30 px-8 pb-6">
        <div className="h-[3px] bg-white/10 relative overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-[#39FF14]"
            animate={{ width: `${(t / TOTAL) * 100}%` }}
            transition={{ duration: 0.05 }}
          />
          {/* Scene segment markers */}
          {STARTS.slice(0, -1).map((s, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 w-px bg-black"
              style={{ left: `${(s / TOTAL) * 100}%` }}
            />
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#8B93A5]">
          <span>SCENE {String(currentIdx + 1).padStart(2, "0")} / 08 · {SCENES[currentIdx].id} — {sceneLabel(currentIdx)}</span>
          <span>QUASECT.COM</span>
        </div>
      </div>

      {!running && (
        <div className="absolute inset-0 z-40 bg-[#050507]/95 grid place-items-center">
          <div className="text-center">
            <LogoLockup variant="dark" size={48} className="justify-center" />
            <h1 className="font-display font-black text-4xl md:text-5xl mt-8 tracking-tighter">
              Quasect Client Reel
            </h1>
            <p className="text-[#8B93A5] mt-3 max-w-md mx-auto">
              100-second pitch reel · 8 scenes · auto-plays once started.
            </p>
            <button
              data-testid="reel-play"
              onClick={() => { setT(0); setRunning(true); }}
              className="mt-8 bg-[#39FF14] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#32E612] transition-colors"
            >
              ▶ Play Reel
            </button>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mt-6">
              Tip: append ?auto=1 to auto-play (used for recording).
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function sceneLabel(i) {
  return [
    "OPENING",
    "VAPT",
    "THREAT PROTECTION",
    "INFRASTRUCTURE",
    "FUTURE OPS",
    "STAFFING",
    "WHITE-LABEL",
    "BRAND REVEAL",
  ][i];
}

// ============ SCENES ============

const CYAN = "#00F0FF";
const NEON = "#39FF14";

function CenterStage({ children, className = "" }) {
  return (
    <div className={`absolute inset-0 flex flex-col items-center justify-center px-12 ${className}`}>
      {children}
    </div>
  );
}

function HeadlineOverline({ text, color = CYAN }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="font-mono text-[12px] tracking-[0.35em] uppercase flex items-center gap-3 mb-6"
      style={{ color }}
    >
      <span className="w-8 h-px" style={{ background: color }} />
      {text}
    </motion.div>
  );
}

function SceneIntro({ p }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* World map dots */}
      <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
        {Array.from({ length: 220 }).map((_, i) => {
          const x = (i * 137) % 1920;
          const y = (i * 211) % 1080;
          const r = 2 + ((i * 17) % 4);
          return <circle key={i} cx={x} cy={y} r={r} fill={i % 11 === 0 ? NEON : "rgba(0,240,255,0.35)"} />;
        })}
        {/* connecting arcs */}
        {Array.from({ length: 18 }).map((_, i) => {
          const x1 = (i * 311) % 1920;
          const y1 = (i * 173) % 1080;
          const x2 = (i * 419) % 1920;
          const y2 = (i * 251) % 1080;
          return (
            <line
              key={"l" + i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={CYAN}
              strokeWidth="0.6"
              strokeOpacity="0.35"
              strokeDasharray="3 6"
            />
          );
        })}
      </svg>

      <motion.div className="absolute inset-0" animate={{ scale: [1, 1.04] }} transition={{ duration: 8, ease: "linear" }}>
        <CenterStage>
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.8 }}>
            <LogoMark size={120} />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="font-black text-7xl md:text-8xl tracking-tighter mt-8 text-center"
          >
            QUASECT<span style={{ color: NEON }}>.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="font-mono text-sm md:text-base tracking-[0.35em] uppercase mt-6"
            style={{ color: CYAN }}
          >
            Next-Gen SIEM &amp; Cybersecurity Platform
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6 }}
            className="text-[#8B93A5] text-lg mt-10 max-w-2xl text-center leading-relaxed"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            In a rapidly evolving digital world, businesses need intelligent
            cybersecurity built for modern IT infrastructure.
          </motion.p>
        </CenterStage>
      </motion.div>

      {/* HUD scanlines */}
      <div className="scan-line" style={{ animationDuration: "5s" }} />
    </div>
  );
}

function MockBarChart({ count = 26, accent = NEON }) {
  return (
    <div className="flex items-end gap-[3px] h-32">
      {Array.from({ length: count }).map((_, i) => {
        const h = 20 + ((i * 53) % 80);
        return (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ delay: i * 0.03, duration: 0.6 }}
            className="flex-1"
            style={{ background: h > 70 ? accent : h > 40 ? CYAN : "rgba(255,255,255,0.25)" }}
          />
        );
      })}
    </div>
  );
}

function PanelHeader({ icon: Icon, label, status }) {
  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-black/40">
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest" style={{ color: CYAN }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-mono text-[10px]" style={{ color: status === "ok" ? NEON : "#F59E0B" }}>
        {status === "ok" ? "● HEALTHY" : "● ATTENTION"}
      </div>
    </div>
  );
}

function SceneVAPT() {
  return (
    <div className="absolute inset-0 px-16 py-24 flex flex-col">
      <HeadlineOverline text="01 / VAPT REPORTING" />
      <motion.h2
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7 }}
        className="font-black text-5xl md:text-6xl tracking-tighter max-w-3xl leading-[1.05]"
      >
        Advanced VAPT <span style={{ color: NEON }}>Reporting.</span>
      </motion.h2>

      <div className="grid grid-cols-12 gap-5 mt-12 flex-1">
        {/* Left: chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="col-span-7 border border-white/10 bg-[#0A0A0D] relative"
        >
          <PanelHeader icon={ShieldCheck} label="THREAT ANALYTICS · CRITICAL" status="ok" />
          <div className="p-5">
            <div className="flex justify-between font-mono text-[10px] text-[#8B93A5] mb-3">
              <span>SEVERITY DISTRIBUTION</span>
              <span style={{ color: NEON }}>+12.4%</span>
            </div>
            <MockBarChart count={28} />
            <div className="grid grid-cols-4 gap-3 mt-5">
              {[
                { l: "CRITICAL", v: "12", c: "#EF4444" },
                { l: "HIGH", v: "47", c: "#F59E0B" },
                { l: "MEDIUM", v: "183", c: CYAN },
                { l: "LOW", v: "402", c: "#8B93A5" },
              ].map((s) => (
                <div key={s.l} className="border border-white/10 p-3">
                  <div className="font-mono text-[9px] text-[#8B93A5]">{s.l}</div>
                  <div className="font-bold text-2xl" style={{ color: s.c }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Right: bullets */}
        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
          className="col-span-5 border border-white/10 bg-[#0A0A0D] p-6"
        >
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-4">
            ENTERPRISE INSIGHTS
          </div>
          {[
            "Real-time vulnerability visibility",
            "Enterprise threat insights",
            "Actionable security analytics",
            "Risk prioritization & remediation",
          ].map((b, i) => (
            <motion.div
              key={b}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 + i * 0.25 }}
              className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: NEON }} />
              <span className="text-white/90">{b}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function SceneThreat({ p }) {
  return (
    <div className="absolute inset-0 px-16 py-24 flex flex-col">
      <HeadlineOverline text="02 / THREAT PROTECTION" />
      <motion.h2
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        className="font-black text-5xl md:text-6xl tracking-tighter max-w-4xl leading-[1.05]"
      >
        Built for proactive <span style={{ color: CYAN }}>cyber defense.</span>
      </motion.h2>

      <div className="grid grid-cols-12 gap-5 mt-12 flex-1">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}
          className="col-span-7 border border-white/10 bg-[#0A0A0D] relative overflow-hidden"
        >
          <PanelHeader icon={Activity} label="LIVE ALERT FEED" status="attn" />
          <div className="p-5 space-y-3 font-mono text-sm">
            {[
              { t: "00:42:11", lvl: "HIGH", msg: "Brute-force attempt blocked · 45.92.x.x", c: "#EF4444" },
              { t: "00:42:08", lvl: "MED", msg: "Anomalous outbound DNS — flagged for review", c: "#F59E0B" },
              { t: "00:42:01", lvl: "INFO", msg: "Patch applied: CVE-2025-30461", c: CYAN },
              { t: "00:41:47", lvl: "HIGH", msg: "Lateral movement detected · isolated host-12", c: "#EF4444" },
              { t: "00:41:30", lvl: "OK", msg: "Endpoint scan complete · 0 threats", c: NEON },
            ].map((a, i) => (
              <motion.div
                key={a.t}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.5 }}
                className="flex items-center gap-4 border border-white/10 px-3 py-2 bg-black/30"
              >
                <span className="text-[#8B93A5]">{a.t}</span>
                <span className="font-bold px-2 py-0.5 text-[10px] tracking-widest"
                      style={{ background: a.c + "22", color: a.c, border: `1px solid ${a.c}66` }}>
                  {a.lvl}
                </span>
                <span className="text-white/90 flex-1">{a.msg}</span>
              </motion.div>
            ))}
          </div>
          {/* AI scanning beam */}
          <motion.div
            className="absolute left-0 right-0 h-1"
            style={{ background: `linear-gradient(90deg, transparent, ${CYAN}, transparent)` }}
            animate={{ top: ["10%", "90%", "10%"] }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
          className="col-span-5 flex flex-col gap-5"
        >
          <div className="border border-white/10 bg-[#0A0A0D] p-6">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-3">
              MTTD · MEAN TIME TO DETECT
            </div>
            <div className="font-black text-6xl tracking-tighter" style={{ color: NEON }}>
              &lt;60s
            </div>
            <p className="text-[#8B93A5] mt-3" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              From signal to triage — by certified L2 analysts.
            </p>
          </div>
          <div className="border border-white/10 bg-[#0A0A0D] p-6 flex-1">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-3">
              CENTRALIZED VISIBILITY
            </div>
            <p className="text-white/90 text-lg leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Monitor vulnerabilities, analyze threats, and strengthen security
              posture through one pane of glass.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function SceneInfra() {
  // Animated network nodes
  const nodes = [
    { x: 200, y: 200, label: "EDGE" },
    { x: 600, y: 150, label: "FW" },
    { x: 1000, y: 220, label: "CORE" },
    { x: 1400, y: 180, label: "CLOUD" },
    { x: 400, y: 480, label: "DB" },
    { x: 800, y: 540, label: "APP" },
    { x: 1200, y: 500, label: "API" },
    { x: 1500, y: 460, label: "K8S" },
  ];
  const edges = [
    [0, 1], [1, 2], [2, 3], [1, 4], [4, 5], [5, 6], [6, 7], [2, 5], [3, 7],
  ];
  return (
    <div className="absolute inset-0 px-16 py-24 flex flex-col">
      <HeadlineOverline text="03 / UPCOMING · INFRASTRUCTURE MONITORING" />
      <motion.h2
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        className="font-black text-5xl md:text-6xl tracking-tighter max-w-4xl leading-[1.05]"
      >
        Live infrastructure, <span style={{ color: NEON }}>everywhere.</span>
      </motion.h2>

      <div className="relative flex-1 mt-10 border border-white/10 bg-[#0A0A0D] overflow-hidden">
        <PanelHeader icon={Network} label="NETWORK TOPOLOGY · LIVE" status="ok" />
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1800 720" preserveAspectRatio="xMidYMid slice">
          {edges.map(([a, b], i) => (
            <motion.line
              key={i}
              x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y}
              stroke={CYAN} strokeWidth="1.5" strokeDasharray="6 8"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.6 }}
              transition={{ delay: 0.4 + i * 0.1, duration: 0.8 }}
            />
          ))}
          {nodes.map((n, i) => (
            <g key={i}>
              <motion.circle
                cx={n.x} cy={n.y} r={28}
                fill="rgba(57,255,20,0.08)" stroke={NEON} strokeWidth="1.5"
                initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 + i * 0.12 }}
                style={{ transformOrigin: `${n.x}px ${n.y}px` }}
              />
              <motion.circle
                cx={n.x} cy={n.y} r={6} fill={NEON}
                animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.15 }}
              />
              <text x={n.x} y={n.y + 52} textAnchor="middle" fill="#8B93A5"
                    fontFamily="'JetBrains Mono', monospace" fontSize="14" letterSpacing="2">
                {n.label}
              </text>
            </g>
          ))}
          {/* pulse moving along edge */}
          {edges.slice(0, 4).map(([a, b], i) => (
            <motion.circle
              key={"p" + i}
              r="5" fill={CYAN}
              animate={{
                cx: [nodes[a].x, nodes[b].x],
                cy: [nodes[a].y, nodes[b].y],
              }}
              transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.5, ease: "linear" }}
            />
          ))}
        </svg>
        <div className="absolute bottom-6 left-6 right-6 grid grid-cols-4 gap-4">
          {[
            { l: "Network Device Monitoring", i: Network },
            { l: "Up & Down Status", i: Activity },
            { l: "Infrastructure Health", i: ServerCog },
            { l: "Real-time Alerting", i: AlertTriangle },
          ].map((b, i) => (
            <motion.div
              key={b.l}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 + i * 0.4 }}
              className="border border-white/15 bg-black/60 backdrop-blur p-3 flex items-center gap-3"
            >
              <b.i className="w-5 h-5" style={{ color: NEON }} />
              <span className="font-mono text-xs tracking-wider uppercase">{b.l}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SceneFutureOps() {
  return (
    <div className="absolute inset-0 px-16 py-24 flex flex-col">
      <HeadlineOverline text="04 / FUTURE IT OPERATIONS" />
      <motion.h2
        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
        className="font-black text-5xl md:text-6xl tracking-tighter max-w-4xl leading-[1.05]"
      >
        A complete <span style={{ color: NEON }}>IT monitoring</span> ecosystem.
      </motion.h2>
      <div className="grid grid-cols-12 gap-5 mt-12 flex-1">
        {[
          { i: Eye, t: "Unified visibility", d: "Cloud, network, endpoint, identity — one console." },
          { i: Cpu, t: "Intelligent automation", d: "Runbooks triggered the moment thresholds are breached." },
          { i: Cloud, t: "Cloud-native scale", d: "From a single tenant to multi-cloud, multi-region." },
          { i: ShieldCheck, t: "Compliance built-in", d: "ISO 27001, SOC 2, PCI-DSS, HIPAA, GDPR." },
        ].map((c, i) => (
          <motion.div
            key={c.t}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.25 }}
            className="col-span-3 border border-white/10 bg-[#0A0A0D] p-6 flex flex-col"
          >
            <c.i className="w-7 h-7 mb-4" style={{ color: CYAN }} />
            <div className="font-bold text-xl">{c.t}</div>
            <p className="text-[#8B93A5] mt-2 text-sm flex-1" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {c.d}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function SceneStaffing() {
  return (
    <div className="absolute inset-0 px-16 py-24 flex flex-col">
      <HeadlineOverline text="05 / IT SUPPORT STAFFING" />
      <motion.h2
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        className="font-black text-5xl md:text-6xl tracking-tighter max-w-4xl leading-[1.05]"
      >
        Trusted, <span style={{ color: NEON }}>scalable IT teams.</span>
      </motion.h2>

      <div className="grid grid-cols-12 gap-5 mt-12 flex-1">
        <motion.div
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          className="col-span-7 border border-white/10 bg-[#0A0A0D] p-7 relative overflow-hidden"
        >
          <Users className="w-10 h-10 mb-5" style={{ color: CYAN }} />
          <div className="space-y-3">
            {[
              "Multiple service providers",
              "Enterprise support teams",
              "Scalable resource management",
              "Professional IT operations",
            ].map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.35 }}
                className="flex items-center gap-3 border-b border-white/5 pb-3"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                <CheckCircle2 className="w-5 h-5" style={{ color: NEON }} />
                <span className="text-lg">{b}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Team grid */}
        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
          className="col-span-5 border border-white/10 bg-[#0A0A0D] p-5 grid grid-cols-4 grid-rows-4 gap-2"
        >
          {Array.from({ length: 16 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 + i * 0.06 }}
              className="aspect-square border flex items-center justify-center font-mono text-[9px] tracking-widest"
              style={{ borderColor: i % 5 === 0 ? NEON : "rgba(255,255,255,0.1)", color: i % 5 === 0 ? NEON : "#8B93A5" }}
            >
              {String(i + 1).padStart(2, "0")}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function SceneWhiteLabel() {
  const brands = ["NORTHWIND", "AETHER", "LUMEN", "ORBIT", "VERTEX", "QUASECT"];
  return (
    <div className="absolute inset-0 px-16 py-24 flex flex-col">
      <HeadlineOverline text="06 / WHITE-LABEL READY" />
      <motion.h2
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        className="font-black text-5xl md:text-6xl tracking-tighter max-w-4xl leading-[1.05]"
      >
        Your brand. <span style={{ color: NEON }}>Our engine.</span>
      </motion.h2>

      <div className="grid grid-cols-12 gap-5 mt-12 flex-1">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="col-span-8 border border-white/10 bg-[#0A0A0D] p-7 relative overflow-hidden"
        >
          {/* Tenant brand morphing */}
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-5">
            ACTIVE TENANT
          </div>
          <div className="grid grid-cols-3 gap-4">
            {brands.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.18 }}
                className="border border-white/15 p-5 flex items-center justify-center bg-black/30"
                style={{ minHeight: 90 }}
              >
                <div className="flex items-center gap-3">
                  <Tag className="w-5 h-5" style={{ color: i === 5 ? NEON : CYAN }} />
                  <span className="font-bold text-xl tracking-tighter" style={{ color: i === 5 ? NEON : "#fff" }}>
                    {b}.
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
          {/* Live indicator */}
          <div className="mt-7 flex items-center gap-3 font-mono text-xs tracking-widest" style={{ color: NEON }}>
            <span className="w-2 h-2 bg-[#39FF14] pulse-dot" />
            6 LIVE TENANTS · MULTI-REGION · ENC TLS-1.3
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
          className="col-span-4 flex flex-col gap-3"
        >
          {[
            "Multi-tenant SaaS architecture",
            "Custom branding & domains",
            "Market-standard dashboard",
            "Promotion for service providers",
          ].map((b, i) => (
            <motion.div
              key={b}
              initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.0 + i * 0.35 }}
              className="border border-white/10 bg-[#0A0A0D] p-4 flex items-center gap-3"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              <Sparkles className="w-4 h-4 shrink-0" style={{ color: CYAN }} />
              <span className="text-sm">{b}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function SceneFinal() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Particle sweep */}
      <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
        {Array.from({ length: 90 }).map((_, i) => {
          const x = (i * 211) % 1920;
          const y = (i * 137) % 1080;
          return <circle key={i} cx={x} cy={y} r="2" fill={i % 7 === 0 ? NEON : "rgba(0,240,255,0.5)"} />;
        })}
      </svg>
      <motion.div
        className="absolute inset-0"
        animate={{ background: ["radial-gradient(circle at center, rgba(57,255,20,0.0), transparent 50%)",
                                "radial-gradient(circle at center, rgba(57,255,20,0.25), transparent 70%)",
                                "radial-gradient(circle at center, rgba(57,255,20,0.0), transparent 50%)"] }}
        transition={{ duration: 5, repeat: Infinity }}
      />
      <CenterStage>
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.9 }}>
          <LogoMark size={140} />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, letterSpacing: "0.4em" }}
          animate={{ opacity: 1, letterSpacing: "-0.04em" }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="font-black text-8xl md:text-9xl tracking-tighter mt-8"
        >
          QUASECT<span style={{ color: NEON }}>.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="font-mono text-sm tracking-[0.35em] uppercase mt-6"
          style={{ color: CYAN }}
        >
          The Future of Cybersecurity &amp; Infrastructure Monitoring
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.0 }}
          className="mt-10 inline-flex items-center gap-4"
        >
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#8B93A5]">
            quasect.com · info@quasect.com
          </span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.6 }}
          className="mt-8 px-6 py-3 border"
          style={{ borderColor: NEON, color: NEON, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.3em" }}
        >
          COMING SOON
        </motion.div>
      </CenterStage>
    </div>
  );
}
